import { checkStatus } from '../../utilities/fetch';
import { createQueryString } from '../../utilities/utilities';
import { jobSearchPath, favoritesPath } from '../../utilities/api_relative_paths';
import { DEFAULT_PAGE_SIZE, FEATURED_JOBS_PAGE_SIZE } from '../constants/job_search.constants';
import {
  SALARY_PERIODICITY_OPTIONS,
  DEFAULT_SALARY_PERIODICITY_OPTION,
  DEFAULT_CURRENCY,
} from '../../utilities/constants/job_related.constants';
import { newslabNewsletterPath } from '../../utilities/api_relative_paths';
import {
  UnformattedSalaryData,
  SalaryData,
  Sorting,
  GetJobsResponse,
  JobsFromAPI,
  Job,
  GetFavoriteJobsResponse,
  QueryParams,
  QueryFilter,
  ToggleFavoriteQueryParams,
  ToggleFavoriteResponse,
} from '../types';
import { Newsletter } from '../../account_newsletters/types/types';
import { getLocalStorageItem, setLocalStorageItem } from '../../utilities/local_storage_management';

const formatSalaryData = (data: UnformattedSalaryData): SalaryData | Record<string, never> => {
  const { periodicity, currency, range_bottom, range_top } = data;
  if (!range_bottom && !range_top) return {};

  const displayedPeriodicity: { value: string; text: string } =
    SALARY_PERIODICITY_OPTIONS.find((option) => option.value === periodicity) ||
    DEFAULT_SALARY_PERIODICITY_OPTION;

  return {
    periodicity: displayedPeriodicity.text,
    currency: currency?.name || DEFAULT_CURRENCY,
    unique_salary_value:
      range_top === range_bottom || !range_bottom ? range_top : !range_top ? range_bottom : null,
    range_bottom,
    range_top,
  };
};

const addIsFavoriteFieldToJobs = (
  jobResponse: GetJobsResponse
): Promise<GetJobsResponse> | GetJobsResponse => {
  if (!jobResponse.data.length) return jobResponse;

  const modifiedJobSet: Job[] = [...jobResponse.data];
  const queryParams: { filter: { object_type: string; object_id: number[] } } = {
    filter: {
      object_type: 'Job',
      object_id: modifiedJobSet.map((job) => job.id),
    },
  };

  return fetch(favoritesPath + createQueryString(queryParams))
    .then(checkStatus)
    .then((response) => response.json())
    .then((response: GetFavoriteJobsResponse) => {
      const favoriteIds = response.data.map((favorite) => favorite.object_id);

      modifiedJobSet.forEach((job) => {
        job.is_favorite = favoriteIds.includes(job.id);
      });

      return {
        ...jobResponse,
        data: modifiedJobSet,
      };
    });
};

const getNonFeaturedJobs = (
  isUserSignedIn: boolean,
  queryParams: QueryParams,
  sorting: Sorting,
  pageNumber = 1,
  userMembership: string | null
): Promise<GetJobsResponse> => {
  const queryFilter: QueryFilter = {
    sorting,
    filter: {
      ...queryParams,
      is_featured: false,
    },
    page: { size: DEFAULT_PAGE_SIZE, number: pageNumber },
    suggest: true,
    options: {
      facets: true,
    },
  };

  const cacheKey =
    jobSearchPath + JSON.stringify({ ...queryParams, is_featured: false, pageNumber, sorting });

  const localStorageItem = getLocalStorageItem<GetJobsResponse>(
    cacheKey,
    120000,
    userMembership || 'UA'
  );
  if (localStorageItem) return Promise.resolve(localStorageItem as GetJobsResponse);

  return fetch(jobSearchPath + createQueryString(queryFilter))
    .then(checkStatus)
    .then((response) => response.json())
    .then((response: JobsFromAPI) => {
      const formattedSalaryData = response.data.map((job) => ({
        ...job,
        salary_data: job.salary_data ? formatSalaryData(job.salary_data) : {},
      }));

      const data = { ...response, data: formattedSalaryData };
      return isUserSignedIn ? addIsFavoriteFieldToJobs(data) : data;
    })
    .then((response: GetJobsResponse) => {
      setLocalStorageItem<GetJobsResponse>(cacheKey, response, userMembership || 'UA');
      return response;
    });
};

const getFeaturedJobs = (
  isUserSignedIn: boolean,
  queryParams: QueryParams,
  sorting: Sorting,
  pageNumber = 1,
  userMembership: string | null
): Promise<GetJobsResponse> => {
  const queryFilter: QueryFilter = {
    sorting,
    filter: {
      ...queryParams,
      is_featured: true,
    },
    page: { size: FEATURED_JOBS_PAGE_SIZE, number: pageNumber },
  };

  const cacheKey =
    jobSearchPath + JSON.stringify({ ...queryParams, is_featured: true, pageNumber, sorting });

  const localStorageItem = getLocalStorageItem<GetJobsResponse>(
    cacheKey,
    120000,
    userMembership || 'UA'
  );
  if (localStorageItem) return Promise.resolve(localStorageItem as GetJobsResponse);

  return fetch(jobSearchPath + createQueryString(queryFilter))
    .then(checkStatus)
    .then((response) => response.json())
    .then((response: JobsFromAPI) => {
      const formattedSalaryData = response.data.map((job) => ({
        ...job,
        salary_data: job.salary_data ? formatSalaryData(job.salary_data) : {},
      }));

      const data = { ...response, data: formattedSalaryData };
      return isUserSignedIn ? addIsFavoriteFieldToJobs(data) : data;
    })
    .then((response: GetJobsResponse) => {
      setLocalStorageItem<GetJobsResponse>(cacheKey, response, userMembership || 'UA');
      return response;
    });
};

const toggleFavorite = (
  id: number | null,
  isFavorite: boolean
): Promise<ToggleFavoriteResponse> => {
  if (!id) return null;

  const queryParams: ToggleFavoriteQueryParams = isFavorite
    ? { object_type: 'Job', object_id: id }
    : {
      favorite: {
        object_type: 'Job',
        object_id: id,
        name: 'favorite',
        folder_name: 'Jobs',
      },
    };
  const url = favoritesPath + createQueryString(queryParams);
  const method = isFavorite ? 'DELETE' : 'POST';

  return fetch(url, { method })
    .then(checkStatus)
    .then((response) => response.json());
};

const getCareerHubNewsletter = (): Promise<Newsletter> => {
  return fetch(`${newslabNewsletterPath}/by_permalink/devex-career-hub`)
    .then(checkStatus)
    .then((response) => response.json());
};

export {
  addIsFavoriteFieldToJobs,
  getNonFeaturedJobs,
  getFeaturedJobs,
  toggleFavorite,
  formatSalaryData,
  getCareerHubNewsletter,
};
